import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CampaignDetailsHeader from "./details/CampaignDetailsHeader";
import CampaignDetailsTabs from "./details/CampaignDetailsTabs";
import CampaignDetailsInfo from "./details/CampaignDetailsInfo";
import CampaignDetailsItemsTab from "./details/CampaignDetailsItemsTab";

export default function CampaignDetails({ open, onClose, campaign, onManage }) {
  const [activeTab, setActiveTab] = useState(0);
  const [totalCountItems, setTotalCountItems] = useState(0);
  const [shouldLoadItems, setShouldLoadItems] = useState(false);
  const [currentCampaignId, setCurrentCampaignId] = useState(null);

  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setShouldLoadItems(false);
      setCurrentCampaignId(null);
      setTotalCountItems(0);
    }
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (
      newValue === 1 &&
      !shouldLoadItems &&
      campaign?.id === currentCampaignId
    ) {
      return;
    } else if (newValue === 0 && campaign?.id) {
      setCurrentCampaignId(campaign.id);
    }
  };

  if (!campaign) return null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "started":
        return "success";
      case "pending":
        return "info";
      case "finished":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("pt-BR");
    } catch {
      return "N/A";
    }
  };

  const detailItems = [
    { label: "ID da Campanha", value: campaign.id || "N/A", type: "text" },
    { label: "Nome", value: campaign.name || "Sem nome", type: "text" },
    {
      label: "Status",
      value: campaign.status || "Desconhecido",
      type: "status",
    },
    { label: "Tipo", value: campaign.type || "N/A", type: "text" },
    {
      label: "Data de Início",
      value: formatDate(campaign.start_date),
      type: "text",
    },
    {
      label: "Data de Fim",
      value: formatDate(campaign.finish_date),
      type: "text",
    },
    {
      label: "Data Limite",
      value: formatDate(campaign.deadline_date),
      type: "text",
    },
  ];
  
  // Add benefits information if available
  if (campaign.benefits) {
    const getBenefitTypeLabel = (type) => {
      switch (type) {
        case 'REBATE':
          return 'Cashback';
        case 'DISCOUNT':
          return 'Desconto';
        default:
          return type;
      }
    };

    const getPaymentMethodLabel = (method) => {
      switch (method) {
        case 'ALL':
          return 'Todos os métodos';
        case 'CREDIT_CARD':
          return 'Cartão de crédito';
        case 'DEBIT_CARD':
          return 'Cartão de débito';
        case 'PIX':
          return 'PIX';
        default:
          return method;
      }
    };

    detailItems.push({
      label: "Tipo de Benefício",
      value: getBenefitTypeLabel(campaign.benefits.type) || "N/A",
      type: "text",
    });

    if (campaign.benefits.meli_percent) {
      detailItems.push({
        label: "Percentual Mercado Livre",
        value: `${campaign.benefits.meli_percent}%`,
        type: "text",
      });
    }

    if (campaign.benefits.seller_percent) {
      detailItems.push({
        label: "Percentual Vendedor",
        value: `${campaign.benefits.seller_percent}%`,
        type: "text",
      });
    }

    if (campaign.benefits.payment_method) {
      detailItems.push({
        label: "Método de Pagamento",
        value: getPaymentMethodLabel(campaign.benefits.payment_method),
        type: "text",
      });
    }
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <CampaignDetailsHeader
          campaign={campaign}
          getStatusColor={getStatusColor}
        />
      </DialogTitle>
      <CampaignDetailsTabs
        activeTab={activeTab}
        handleTabChange={handleTabChange}
      />
      <DialogContent
        dividers
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {activeTab === 0 && (
          <CampaignDetailsInfo
            detailItems={detailItems}
            getStatusColor={getStatusColor}
          />
        )}
        {activeTab === 1 && (
          <CampaignDetailsItemsTab
            campaign={campaign}
            setTotalCountItems={setTotalCountItems}
          />
        )}
      </DialogContent>
      <DialogActions
        sx={{ p: 3, gap: 2, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Box sx={{ display: "flex", flexGrow: 1, alignItems: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              color: "grey.600",
              gap: 1,
            }}
          >
            <InfoIcon fontSize="small" />{" "}
            <Typography variant="body2" color="text.secondary">
              {activeTab === 1
                ? `Total de ${totalCountItems || 0} itens da campanha`
                : `Campanha ${campaign?.status || "ativa"}`}
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            color: "grey.600",
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          Fechar
        </Button>
        <Button
          variant="contained"
          onClick={onManage}
          sx={{
            bgcolor: "#3483fa",
            "&:hover": { bgcolor: "#2968c8" },
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            borderRadius: 2,
          }}
        >
          Gerenciar Campanha
        </Button>
      </DialogActions>
    </Dialog>
  );
}
